/* tmc-product.js — panel chooser and patient-form hand-off for the test product pages.
   Spec: docs/spec/product-pages.md §2.1, §2.3, §2.4. Ticket T-04; patient-form wiring: wayfinder #42.

   Every panel is already in the DOM, so selecting one is synchronous: no fetch, no loading
   state, no layout shift. There is no quantity control and no direct /cart/add.js POST here —
   docs/form-contract.md §2 requires one patient-details form per test, so Add to cart opens the
   frozen patient-details sheet (assets/tmc-patient-form.js) instead of adding straight to cart.
   A checked add-on that counts as a test (the default) rides along in that same form submission,
   getting the same patient's five properties. A checked add-on flagged an accessory
   (metafields.custom.tmc_accessory) carries no patient data and is not something the frozen form
   can post — it is added in a second, plain request after the form's own add succeeds. */

(function () {
  'use strict';

  var MONEY_FALLBACK = '';

  function init(root) {
    // Canonical: the five gut handles share this page (spec §2.2). A JSON template cannot
    // write into <head>, so the section carries the URL and it is applied here.
    if (root.dataset.canonicalUrl) {
      var canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.href = new URL(root.dataset.canonicalUrl, window.location.origin).href;
    }

    var form = root.querySelector('[data-tmc-cart-form]');
    if (!form) return;

    var variantInput = form.querySelector('[data-tmc-variant-input]');
    var ctaPrice = form.querySelector('[data-tmc-cta-price]');
    var addButton = form.querySelector('[data-tmc-add]');
    var stage = root.querySelector('[data-tmc-stage]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-tmc-thumb]'));
    var panels = Array.prototype.slice.call(form.querySelectorAll('[data-tmc-panel]'));

    /* ---------- panel selection ---------- */

    function selectPanel(input, pushUrl) {
      if (!input) return;

      // 1. the variant the form will post, and the name/price the patient-form sheet reads
      if (variantInput) {
        variantInput.value = input.dataset.variantId;
        variantInput.dataset.name = input.dataset.name || '';
        variantInput.dataset.priceCents = input.dataset.priceCents || '0';
      }

      // 2. the price in the CTA. The em dash is the default so the 2.x heroes keep it; the Gut 3.0
      // buy box sets data-price-prefix="" because its board draws the price with no dash at 390 and
      // the section renders one string at every width (NW-37).
      if (ctaPrice) {
        var prefix = ctaPrice.dataset.pricePrefix === undefined ? '— ' : ctaPrice.dataset.pricePrefix;
        ctaPrice.textContent = prefix + (input.dataset.price || MONEY_FALLBACK);
      }

      // 3. the gallery image. The 2.x hero has a single stage image, swapped here. The PDP 3.0 heroes
      // have no such element: they render the shared gallery (assets/tmc-pdp-gallery.js), which holds
      // every tier's images already and is told which tier is on show, nothing else. The event is the whole seam:
      // this file never reads the gallery and the gallery never reads the form.
      if (stage && input.dataset.image && stage.tagName === 'IMG') {
        stage.src = input.dataset.image;
        stage.alt = input.dataset.name || '';
      }
      root.dispatchEvent(new CustomEvent('tmc:panel-selected', { detail: { panelId: input.dataset.panelId } }));

      // 4. selected ring on the row and on the matching thumbnail. The row's ring is CSS
      // (:checked + label); the thumbnail sits outside the form, so it is set here.
      var id = input.dataset.panelId;
      thumbs.forEach(function (thumb) {
        thumb.classList.toggle('is-selected', thumb.dataset.panelId === id);
      });

      // 5. the URL, so the panel deep-links and can be shared or reloaded.
      // Last, and guarded: replaceState throws a SecurityError wherever the document's
      // origin cannot own that path — a sandboxed iframe, a data: URL, and the theme
      // editor's cross-origin frame. The swap above must not depend on it.
      if (pushUrl && input.dataset.url && window.history && window.history.replaceState) {
        try {
          window.history.replaceState(null, '', input.dataset.url);
        } catch (err) {
          /* deep-linking is a nicety; the selection itself has already happened */
        }
      }
    }

    panels.forEach(function (input) {
      input.addEventListener('change', function () {
        if (input.checked) selectPanel(input, true);
      });
    });

    /* Native radios already handle arrow keys within a fieldset, and Enter inside a form
       already submits. Both are left to the browser rather than reimplemented. */

    /* ---------- patient-details hand-off (form-contract.md §2, §3) ---------- */

    var sheet = root.querySelector('[data-tmc-sheet]');
    var sheetTitle = sheet && sheet.querySelector('.tmc-hero-sheet__title');
    var sheetPanel = sheet && sheet.querySelector('[role="dialog"]');
    var opener = null;
    var closeTimer = 0;

    function reducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function focusables() {
      if (!sheetPanel) return [];
      return Array.prototype.filter.call(
        sheetPanel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        function (el) { return !el.closest('[hidden]'); }
      );
    }
    function onKeydown(event) {
      if (!sheet || sheet.hidden) return;
      if (event.key === 'Escape') { event.preventDefault(); closeSheet(); return; }
      if (event.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) { event.preventDefault(); if (sheetTitle) sheetTitle.focus(); return; }
      var i = f.indexOf(document.activeElement);
      if (event.shiftKey && (i <= 0 || !sheetPanel.contains(document.activeElement))) { event.preventDefault(); f[f.length - 1].focus(); }
      else if (!event.shiftKey && (i === f.length - 1 || !sheetPanel.contains(document.activeElement))) { event.preventDefault(); f[0].focus(); }
    }
    function openSheet(from) {
      if (!sheet) return;
      if (closeTimer) { window.clearTimeout(closeTimer); closeTimer = 0; }
      opener = from || null;
      if (patientForm) patientForm.refresh();
      sheet.hidden = false;
      void sheet.offsetWidth;
      sheet.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown);
      if (sheetTitle) sheetTitle.focus();
    }
    function closeSheet() {
      if (!sheet || sheet.hidden) return;
      sheet.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      closeTimer = window.setTimeout(function () { sheet.hidden = true; closeTimer = 0; }, reducedMotion() ? 0 : 340);
      if (opener && typeof opener.focus === 'function') opener.focus();
      opener = null;
    }
    if (sheet) {
      Array.prototype.forEach.call(sheet.querySelectorAll('[data-tmc-sheet-close]'), function (el) {
        el.addEventListener('click', closeSheet);
      });
    }

    /* Every checked test-shaped add-on rides in the SAME patient-form submission as the primary
       test — form-contract.md §2: duplicate the one patient's properties onto its own line. A
       true accessory (data-accessory="true") carries no patient data and is not something the
       frozen form's getSelected() model can express; it is queued and added separately once the
       primary submission succeeds. */
    function selectedForForm() {
      var items = [];
      if (variantInput && variantInput.value) {
        items.push({
          variantId: variantInput.value,
          name: variantInput.dataset.name || '',
          priceCents: parseInt(variantInput.dataset.priceCents, 10) || 0
        });
      }
      form.querySelectorAll('[data-tmc-addon]:checked').forEach(function (addon) {
        if (addon.dataset.accessory === 'true') return;
        items.push({
          variantId: addon.value,
          name: addon.dataset.name || '',
          priceCents: parseInt(addon.dataset.priceCents, 10) || 0
        });
      });
      return items;
    }

    function accessoryIdsChecked() {
      var ids = [];
      form.querySelectorAll('[data-tmc-addon]:checked').forEach(function (addon) {
        if (addon.dataset.accessory === 'true') ids.push(Number(addon.value));
      });
      return ids;
    }

    /* Fires after the patient form's own /cart/add.js has already landed and opened the drawer.
       A second, plain request (no properties — accessories carry no patient data) for anything
       flagged an accessory, then a cart:added nudge so the drawer (assets/tmc-cart.js, which
       re-reads /cart.js on that event) shows everything in one place. */
    function addAccessories(ids) {
      if (!ids.length || !window.fetch) return;
      fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: ids.map(function (id) { return { id: id, quantity: 1 }; }) })
      })
        .then(function () { document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true })); })
        .catch(function () { /* the primary test is already in the cart; an accessory add failing here is not fatal */ });
    }

    var patientForm = null;
    var tpfRoot = sheet && sheet.querySelector('[data-tpf]');
    if (tpfRoot && window.TMCPatientForm) {
      patientForm = window.TMCPatientForm.mount(tpfRoot, {
        getSelected: selectedForForm,
        onEmpty: closeSheet,
        onSuccess: function () {
          var pendingAccessories = accessoryIdsChecked();
          closeSheet();
          addAccessories(pendingAccessories);
        }
      });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (!variantInput || !variantInput.value) return;
      openSheet(addButton);
    });

    // Reflect whatever is checked on load, without rewriting the URL.
    var checked = form.querySelector('[data-tmc-panel]:checked');
    if (checked) selectPanel(checked, false);
  }

  function boot() {
    document.querySelectorAll('[data-tmc-product-hero]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders a section without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    var hero = event.target.querySelector('[data-tmc-product-hero]');
    if (hero) init(hero);
  });
})();
